// Currency utility functions for formatting prices

// Currency symbols mapping
const CURRENCY_SYMBOLS = {
  'GBP': '£',
  'USD': '$',
  'EUR': '€',
  'CAD': 'C$',
  'AUD': 'A$'
};

// Currency names mapping
const CURRENCY_NAMES = {
  'GBP': 'British Pound',
  'USD': 'US Dollar',
  'EUR': 'Euro',
  'CAD': 'Canadian Dollar',
  'AUD': 'Australian Dollar'
};

/**
 * Format a price with currency symbol
 * @param {number} amount - The price amount
 * @param {string} currencyCode - The currency code (GBP, USD, EUR, etc.)
 * @param {string} suffix - Optional suffix (e.g., "per player")
 * @returns {string} Formatted price string
 */
export const formatPrice = (amount, currencyCode = 'GBP', suffix = '') => {
  if (!amount || amount <= 0) return '';
  
  const symbol = CURRENCY_SYMBOLS[currencyCode] || '£';
  const formattedAmount = parseFloat(amount).toFixed(2);
  
  if (suffix) {
    return `${symbol}${formattedAmount} ${suffix}`;
  }
  
  return `${symbol}${formattedAmount}`;
};

/**
 * Format price for display in session cards
 * @param {number} amount - The price amount
 * @param {string} currencyCode - The currency code
 * @param {string} suffix - Optional suffix
 * @returns {string} Formatted price string
 */
export const formatPriceDisplay = (amount, currencyCode = 'GBP', suffix = '') => {
  return formatPrice(amount, currencyCode, suffix);
};

/**
 * Get currency symbol for a currency code
 * @param {string} currencyCode - The currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode = 'GBP') => {
  return CURRENCY_SYMBOLS[currencyCode] || '£';
};

/**
 * Get currency name for a currency code
 * @param {string} currencyCode - The currency code
 * @returns {string} Currency name
 */
export const getCurrencyName = (currencyCode = 'GBP') => {
  return CURRENCY_NAMES[currencyCode] || 'British Pound';
};

/**
 * Get all available currency codes
 * @returns {Array} Array of currency codes
 */
export const getAvailableCurrencies = () => {
  return Object.keys(CURRENCY_SYMBOLS);
};

/**
 * Validate if a currency code is supported
 * @param {string} currencyCode - The currency code to validate
 * @returns {boolean} True if supported
 */
export const isValidCurrencyCode = (currencyCode) => {
  return Object.keys(CURRENCY_SYMBOLS).includes(currencyCode);
};
